import React, { useEffect, useMemo, useState } from "react";
import {
  FiMapPin,
  FiExternalLink,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import ApiServices from "../services/apiservices";
import CustomSpinner from "../components/customspinner";

export default function SearchJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedJobType, setSelectedJobType] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 6;

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await ApiServices.getRequest("/jobs/open-jobs.php");
             
        if (res.success) {
          setJobs(res.jobs);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Unique filter values
  const teams = [...new Set(jobs.map((job) => job.team).filter(Boolean))];
  const locations = [
    ...new Set(jobs.map((job) => job.location).filter(Boolean)),
  ];
  const jobTypes = [
    ...new Set(jobs.map((job) => job.jobType).filter(Boolean)),
  ];  

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesTeam =
        !selectedTeam || job.team === selectedTeam;

      const matchesLocation =
        !selectedLocation || job.location === selectedLocation;

      const matchesJobType =
        !selectedJobType || job.jobType === selectedJobType;

      return matchesTeam && matchesLocation && matchesJobType;
    });
  }, [jobs, selectedTeam, selectedLocation, selectedJobType]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTeam, selectedLocation, selectedJobType]);

  // Pagination logic
  const indexOfLast = currentPage * jobsPerPage;
  const indexOfFirst = indexOfLast - jobsPerPage;

  const currentJobs = filteredJobs.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
   
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      {/* Header section */}
      <section className="bg-green-900 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-yellow-400">
            Current Openings
          </h1>

          <p className="text-lg md:text-xl font-light opacity-90 max-w-2xl mx-auto">
            Discover roles that align with your passion and skills.
            Join us in building the future.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-4 pt-10">
        <div className="max-w-6xl mx-auto bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="grid md:grid-cols-3 gap-4">
            
            {/* Team Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Team
              </label>

              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              >
                <option value="">All Teams</option>

                {teams.map((team, idx) => (
                  <option key={idx} value={team}>
                    {team}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Location
              </label>

              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              >
                <option value="">All Locations</option>

                {locations.map((location, idx) => (
                  <option key={idx} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            {/* Job Type Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Job Type
              </label>

              <select
                value={selectedJobType}
                onChange={(e) => setSelectedJobType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              >
                <option value="">All Job Types</option>

                {jobTypes.map((type, idx) => (
                  <option key={idx} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <CustomSpinner />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-2xl font-semibold text-gray-700 mb-2">
                No Matching Jobs Found
              </h3>

              <p className="text-gray-500">
                Try changing your filters or check back later.
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow flex flex-col h-full"
                  >
                    <div className="mb-4">
                      {/* Team */}
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full mb-3">
                        <span className="text-gray-900">
                          Team:
                        </span>{" "}
                        {job.team}
                      </span>

                      {/* Job Type */}
                      {job.jobType && (
                        <div className="mt-2">
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                            {job.jobType}
                          </span>
                        </div>
                      )}

                      <h3 className="text-xl font-bold text-gray-900 mb-2 mt-4 line-clamp-2">
                        {job.title}
                      </h3>

                      <div className="flex items-center text-gray-500 text-sm gap-2 mt-2">
                        <FiMapPin />
                        <span>{job.location}</span>
                      </div>

                      {/* Internship Duration or Deadline */}
                      <div className="mt-3 text-sm text-gray-600">
                        {job.jobType?.toLowerCase() === "internship" ? (
                          <p>
                            <span className="font-semibold">
                              Duration:
                            </span>{" "}
                            {job.duration || "Not specified"}
                          </p>
                        ) : (
                          <p>
                            <span className="font-semibold">
                              Application Deadline:
                            </span>{" "}
                            {job.applicationDeadline || "Not specified"}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-grow">
                      {job.description}
                    </p>

                    <Link
                      to={`/job/${job.id}`}
                      className="mt-auto flex items-center justify-center gap-2 w-full py-3 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
                    >
                      View Details <FiExternalLink />
                    </Link>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-12 gap-3 flex-wrap">
                  
                  {/* Previous */}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.max(1, p - 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 rounded-full border border-gray-200 disabled:opacity-50 hover:bg-gray-100 transition-colors"
                  >
                    <FiChevronLeft size={22} />
                  </button>

                  {/* Page Numbers */}
                  {Array.from(
                    { length: totalPages },
                    (_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`w-10 h-10 rounded-full font-semibold transition-colors ${
                          currentPage === index + 1
                            ? "bg-yellow-500 text-black"
                            : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {index + 1}
                      </button>
                    )
                  )}

                  {/* Next */}
                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(totalPages, p + 1)
                      )
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full border border-gray-200 disabled:opacity-50 hover:bg-gray-100 transition-colors"
                  >
                    <FiChevronRight size={22} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}